import type {EligibilityReason} from './controlState';

export interface TaskEligibilityInput {
	readOnly: {isReadOnly: boolean} | null;
	// Whether this person may edit tasks at all — the "Edit tasks" permission group, which every
	// mounting surface can read without a viewer prop. Optional, and read as held when absent:
	// missing information must not disable a control, so a surface that has not been taught to pass
	// it keeps the behaviour it had.
	canEditTask?: boolean;
	// From Util.isClientTaskActionsRestricted(viewer) — real, already computed at every one of
	// this control's mount points for their own disabled/locked logic.
	isClientActionsRestricted: boolean;
}

// Fixed order, not a scoring system — kept as a literal list so it stays legible. A locked task is
// reported ahead of the permission because it is the more specific fact and the one that can
// change: unlocking is something the person can watch happen, where a permission is granted by
// somebody else.
const PRIORITY: EligibilityReason[] = ['task_locked', 'no_edit_task'];

export function deriveEligibilityFromTask(task: TaskEligibilityInput): EligibilityReason | null {
	// Two checks, one reason. They sit on different axes — the permission is granted on a role
	// profile, the restriction applies to a client user acting on tasks at all — and a client user
	// can hold the permission and still be restricted. Reporting only the permission would open the
	// agent to somebody who cannot do the same work by hand, which is the whole point of the gate.
	// One reason rather than two because the person's remedy is identical either way: ask an
	// administrator. Two reasons would ask them to tell apart two things they cannot act on.
	const cannotAct = task.canEditTask === false || task.isClientActionsRestricted;
	const applicable: Partial<Record<EligibilityReason, boolean>> = {
		task_locked: !!task.readOnly?.isReadOnly,
		no_edit_task: cannotAct,
	};
	return PRIORITY.find(reason => applicable[reason]) ?? null;
}
